import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Label, LABEL_TYPE, ProgressBar } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import {
  STORAGE_LIMIT_EXCEEDED_ERROR_CODE,
  TIME_LIMIT_EXCEEDED_ERROR_CODE,
  TRIBUTE_VIDEO_LIMIT_EXCEEDED_ERROR_CODE
} from '@/constants';
import { AuthContext } from '@/contexts';
import { AssetStatus, PERMISSION_TYPE, TributeVideoExportStatus } from '@/enums';
import { getMessageForProgressBar } from '@/helpers/TributeVideoProgressHelper';
import { showToast, ToastType } from '@/helpers/toast';
import { useGenerateTributeVideo, useGetTributeVideoQuery } from '@/services';
import {
  useCancelVideoRendering,
  useGetTributeVideoExportStatus
} from '@/services/tributeVideoExport.service';
import { TributeVideoStatusType } from '@/types';

type TributeVideosDetailsProps = {
  id: number;
  setSelectedTributeVideo: Dispatch<SetStateAction<string | undefined>>;
  setAssetDetailsLabel?: Dispatch<SetStateAction<string | undefined>>;
  setSelectedTributeVideoName?: Dispatch<SetStateAction<string | undefined>>;
};

export const TributeVideoDetails = ({
  id,
  setAssetDetailsLabel,
  setSelectedTributeVideo,
  setSelectedTributeVideoName
}: TributeVideosDetailsProps) => {
  const [t] = useTranslation('common');
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { permissions } = useContext(AuthContext);
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);
  const [isCompleted, setIsCompleted] = useState<boolean>();
  const { data: tributeVideo, refetch: refetchTributeVideoData } = useGetTributeVideoQuery(id);
  const [isGenerateAgainButtonDisabled, setIsGenerateAgainButtonDisabled] = useState(false);
  const [isCancelButtonDisabled, setIsCancelButtonDisabled] = useState<boolean>(false);

  const nonCompletedAndNonFailedVideos =
    tributeVideo?.data.exports?.filter(
      (video: TributeVideoStatusType) =>
        video?.status !== TributeVideoExportStatus.Completed &&
        video?.status !== TributeVideoExportStatus.Failed &&
        video?.status !== TributeVideoExportStatus.Canceled
    ) ?? [];

  const sortedVideos = nonCompletedAndNonFailedVideos.sort(
    (a: TributeVideoStatusType, b: TributeVideoStatusType) => Number(b?.id) - Number(a?.id)
  );

  const tributeVideoInProgress = sortedVideos.find(
    (video: TributeVideoStatusType) => video?.isPreview === false
  );
  const previewVideoInProgress = sortedVideos.find(
    (video: TributeVideoStatusType) => video?.isPreview === true
  );

  const videoToShowProgress = tributeVideoInProgress ?? previewVideoInProgress;

  const refetchInterval = useMemo(() => (isCompleted ? 0 : 5000), [isCompleted]);

  const { data: tributeVideoExportData } = useGetTributeVideoExportStatus(
    Number(videoToShowProgress?.id),
    {
      refetchInterval,
      onSuccess: (response) => {
        if (response?.data?.status === TributeVideoExportStatus.Completed) {
          setIsCompleted(true);
        }
      },
      onError: () => ({})
    }
  );

  const { mutateAsync: generateTributeVideo } = useGenerateTributeVideo(queryClient, {
    onSuccess: () => {
      refetchTributeVideoData();
    },
    onError: (error) => {
      setIsGenerateAgainButtonDisabled(false);
      const backendError = error.response?.data?.ErrorMessages?.[0];
      const message = backendError?.DisplayMessage ?? t('regenerateTributeVideoError');
      const errorCode = backendError?.Code;
      const isLimitExceeded =
        !!errorCode &&
        [
          TRIBUTE_VIDEO_LIMIT_EXCEEDED_ERROR_CODE,
          STORAGE_LIMIT_EXCEEDED_ERROR_CODE,
          TIME_LIMIT_EXCEEDED_ERROR_CODE
        ].includes(errorCode);

      if (isLimitExceeded) {
        showToast(ToastType.WARNING, {
          text: message,
          autoClose: false
        });
        return;
      }

      showToast(ToastType.ERROR, message);
    }
  });

  const { mutateAsync: cancelVideoRendering } = useCancelVideoRendering(queryClient, {
    onSuccess: () => {
      refetchTributeVideoData();
    },
    onError: () => undefined
  });

  useEffect(() => {
    setAssetDetailsLabel?.('tributeVideo');
    refetchTributeVideoData();
  }, [tributeVideoExportData?.data, videoToShowProgress?.status]);

  const showVideo = () => {
    const videoUrl = tributeVideo?.data?.tributeVideoUrl ?? tributeVideo?.data?.previewVideoUrl;

    if (tributeVideo?.data?.tributeVideoUrl) {
      setSelectedTributeVideo(videoUrl);
      setSelectedTributeVideoName?.(tributeVideo?.data?.name);
    }

    if (videoUrl) {
      return (
        <div className='flex justify-center overflow-hidden'>
          <video src={videoUrl} controls className='h-80 w-[560px] rounded' />
        </div>
      );
    }

    return null;
  };

  const handleOnClick = () => {
    hasOrgAccess
      ? navigate(`/events/${eventId}/create-video/${tributeVideo?.data.id}`)
      : navigate(
          `/events/${eventId}/upload-content/tribute-videos/create/${tributeVideo?.data.id}`
        );
  };

  const handleRegenerateVideo = async () => {
    setIsGenerateAgainButtonDisabled(true);

    try {
      const latestExport = tributeVideo?.data?.exports?.slice(-1)[0];

      if (!latestExport) {
        return;
      }

      const isPreview = latestExport.isPreview;

      await generateTributeVideo({
        id: tributeVideo?.data?.id,
        isPreview
      });

      refetchTributeVideoData?.();
    } finally {
      setIsGenerateAgainButtonDisabled(false);
    }
  };

  const getAssetContent = () => {
    let assetContent = null;

    switch (tributeVideo?.data.status) {
      case AssetStatus.COMPLETED:
      case AssetStatus.PREVIEW_CREATED:
        assetContent = showVideo();
        break;
      case AssetStatus.CONTENT_PREPARING:
        assetContent = (
          <div className='flex w-full flex-col gap-y-2.5'>
            <Label text={tributeVideo.data.name} type={LABEL_TYPE.PARAGRAPH1_BOLD} />
            <div className='flex flex-col items-center justify-center gap-y-5'>
              <Label
                text={t('tributeVideoDetailsTextFirst')}
                type={LABEL_TYPE.PARAGRAPH1}
                className='break-words !text-primary-black'
              />
              <Label
                text={t('tributeVideoDetailsTextSecond')}
                type={LABEL_TYPE.PARAGRAPH1}
                className='w-[402px] whitespace-normal text-center !text-primary-black'
              />
              <Button
                type='submit'
                className='w-full md:w-fit'
                variant={ButtonVariant.PRIMARY}
                handleOnClick={handleOnClick}>
                {t('continueEditing')}
              </Button>
            </div>
          </div>
        );
        break;
      case AssetStatus.PREVIEW_RENDERING:
      case AssetStatus.VIDEO_RENDERING:
        assetContent = (
          <div className='flex h-72 w-full flex-col items-center justify-center gap-y-2.5 overflow-hidden md:w-[600px]'>
            <Label
              text={t('generateTributeVideo')}
              type={LABEL_TYPE.PARAGRAPH1}
              className='flex w-full justify-center whitespace-normal px-5 text-center !text-primary-black md:w-[400px]'
            />
            <div className='flex w-full md:w-[400px]'>
              <ProgressBar
                progress={tributeVideoExportData?.data.progress}
                message={getMessageForProgressBar(
                  tributeVideoExportData?.data.status,
                  tributeVideoExportData?.data.progress
                )}
                isIndeterminateMode={
                  videoToShowProgress?.status === TributeVideoExportStatus.Preparing ||
                  videoToShowProgress?.status === TributeVideoExportStatus.Pending ||
                  videoToShowProgress?.status === TributeVideoExportStatus.RenderingCompleted
                }
              />
            </div>
            <Button
              variant={ButtonVariant.PRIMARY}
              disabled={isCancelButtonDisabled}
              handleOnClick={() => {
                setIsCancelButtonDisabled(true);
                cancelVideoRendering(tributeVideoExportData?.data.id);
              }}
              className='mt-5'>
              {t('cancelRendering')}
            </Button>
          </div>
        );
        break;
      case AssetStatus.CONTENT_PREPARED:
        assetContent = (
          <div className='flex w-full flex-col gap-y-2.5'>
            <Label text={tributeVideo.data.name} type={LABEL_TYPE.PARAGRAPH1_BOLD} />
            <div className='flex h-72 flex-col items-center justify-center gap-y-5'>
              <Label
                text={t('contentPreparedText')}
                type={LABEL_TYPE.PARAGRAPH1}
                className='w-[402px] whitespace-normal text-center !text-primary-black'
              />
              <Button
                type='submit'
                className='w-full md:w-fit'
                variant={ButtonVariant.PRIMARY}
                handleOnClick={handleOnClick}>
                {t('continueEditing')}
              </Button>
            </div>
          </div>
        );
        break;
      case AssetStatus.FAILED:
        assetContent = (
          <div className='flex w-full flex-col gap-y-2.5'>
            <Label text={tributeVideo.data.name} type={LABEL_TYPE.PARAGRAPH1_BOLD} />
            <div className='flex h-60 flex-col items-center justify-center gap-y-5'>
              <Label
                text={t('tributeVideoRenderingFailed')}
                type={LABEL_TYPE.PARAGRAPH1}
                className='w-[402px] whitespace-normal text-center !text-primary-black'
              />
              <Button
                type='submit'
                className='w-full md:w-fit'
                variant={ButtonVariant.PRIMARY}
                handleOnClick={handleRegenerateVideo}
                disabled={isGenerateAgainButtonDisabled}>
                {t('generateAgain')}
              </Button>
            </div>
          </div>
        );
        break;
      default:
        assetContent = null;
        break;
    }
    return assetContent;
  };

  return (
    <div>
      <div className='flex size-full justify-center'>{getAssetContent()}</div>
    </div>
  );
};
